import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CALL_ME_NOW_EVENT } from "@/lib/openCallMeNow";
import { estimateNote, readServicesEstimate } from "@/hooks/useServicesEstimate";

/**
 * CallMeNowModal — branded "Talk to us now" form.
 *
 * UI is fully custom (our styling/copy). On submit, we POST to our
 * `close-lead` Supabase Edge Function, which creates a Close lead. A
 * Close-side workflow on that lead is what triggers the inbound AI voice
 * agent dial.
 *
 * US-only: phone field is locked to +1 with a non-editable prefix; we accept
 * exactly 10 US digits and write `+1XXXXXXXXXX` into the Close phone input.
 */

const CLOSE_LEAD_ENDPOINT =
  "https://dvddfvigkzikieuqrtcn.supabase.co/functions/v1/close-lead";
const SUPABASE_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR2ZGRmdmlna3ppa2lldXFydGNuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY4NzAxMTgsImV4cCI6MjA5MjQ0NjExOH0.DiSDka0k9Ig6jYjC1xiXfgiQO6KPNYh5BTUGfeZbfc8";

const PALETTE = {
  ink: "#1a1a1a",
  ink2: "#3a3530",
  muted: "#5a5249",
  amber: "#C4823A",
  amberSoft: "#E8B458",
  amberInk: "#A66A28",
  card: "#FFFFFF",
  line: "rgba(0,0,0,0.08)",
  fieldBg: "#FAF7F2",
  fieldLine: "rgba(0,0,0,0.14)",
  danger: "#B3261E",
};
const FONT_BODY = "Barlow, -apple-system, sans-serif";
const FONT_DISPLAY = "'Instrument Serif', 'Cormorant Garamond', Georgia, serif";
const FONT_MONO = "'JetBrains Mono', ui-monospace, monospace";

function track(event: string, props?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  try {
    // @ts-expect-error optional gtag
    window.gtag?.("event", event, props ?? {});
  } catch { /* noop */ }
}

function onlyDigits(s: string) {
  return s.replace(/\D+/g, "");
}

function formatUsPhone(raw: string) {
  const d = onlyDigits(raw).slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function isValidUsPhone(raw: string) {
  const d = onlyDigits(raw);
  if (d.length !== 10) return false;
  // NANP: area code & exchange code must start 2-9
  if (d[0] < "2" || d[3] < "2") return false;
  return true;
}

function isValidEmail(s: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s.trim());
}

type Status = "idle" | "submitting" | "success" | "error";

const fieldBase: React.CSSProperties = {
  width: "100%",
  padding: "12px 14px",
  background: PALETTE.fieldBg,
  border: `1px solid ${PALETTE.fieldLine}`,
  borderRadius: 12,
  fontSize: 15,
  fontFamily: FONT_BODY,
  color: PALETTE.ink,
  outline: "none",
  transition: "border-color 160ms ease, box-shadow 160ms ease, background 160ms ease",
  minHeight: 48,
  boxSizing: "border-box",
};
const labelBase: React.CSSProperties = {
  display: "block",
  fontSize: 11,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: PALETTE.muted,
  fontFamily: FONT_MONO,
  fontWeight: 600,
  marginBottom: 6,
};

export function CallMeNowModal() {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState<string>("unknown");
  const [status, setStatus] = useState<Status>("idle");
  const [errMsg, setErrMsg] = useState<string>("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [business, setBusiness] = useState("");
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState<{ [k: string]: boolean }>({});
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  // Listen for the global open event.
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ source?: string }>;
      setSource(ce.detail?.source ?? "unknown");
      setStatus("idle");
      setErrMsg("");
      setOpen(true);
    };
    window.addEventListener(CALL_ME_NOW_EVENT, handler);
    return () => window.removeEventListener(CALL_ME_NOW_EVENT, handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    track("call_me_now_opened", { source });
    // Focus first field shortly after dialog opens.
    const t = window.setTimeout(() => firstFieldRef.current?.focus(), 120);
    return () => window.clearTimeout(t);
  }, [open, source]);

  const nameValid = fullName.trim().length >= 2;
  const phoneValid = isValidUsPhone(phone);
  const emailValid = isValidEmail(email);
  const canSubmit = status !== "submitting" && nameValid && phoneValid && emailValid;
  const showNameErr = touched.name && !nameValid;
  const showPhoneErr = touched.phone && !phoneValid;
  const showEmailErr = touched.email && !emailValid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ name: true, phone: true, email: true });
    if (!nameValid || !phoneValid || !emailValid) return;
    if (status === "submitting") return;
    setStatus("submitting");
    setErrMsg("");
    try {
      const phoneE164 = `+1${onlyDigits(phone)}`;
      const note = estimateNote(readServicesEstimate());
      const res = await fetch(CLOSE_LEAD_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          firstName: fullName.trim(),
          phone: phoneE164,
          email: email.trim(),
          business: business.trim(),
          source,
          ...(note ? { note } : {}),
          pageUrl: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : "",
        }),
      });
      let json: { ok?: boolean; error?: unknown } = {};
      try { json = await res.json(); } catch { /* noop */ }
      if (!res.ok || !json?.ok) {
        throw new Error(`close_lead_failed_${res.status}`);
      }
      setStatus("success");
      track("call_me_now_submitted", { source });
    } catch (err) {
      setErrMsg(
        "We couldn't connect right now. Try again or email hi@hireeva.io.",
      );
      setStatus("error");
      track("call_me_now_failed", {
        source,
        reason: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      // Reset form a beat after close so the success state isn't visible during close anim.
      window.setTimeout(() => {
        setFullName("");
        setPhone("");
        setBusiness("");
        setEmail("");
        setTouched({});
        setErrMsg("");
        setStatus("idle");
      }, 220);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[480px]"
        style={{
          background: PALETTE.card,
          border: `1px solid ${PALETTE.line}`,
          borderRadius: 20,
          padding: 0,
          overflow: "hidden",
          maxWidth: 480,
        }}
      >
        <div aria-hidden style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: `radial-gradient(circle at 90% 0%, ${PALETTE.amberSoft}18, transparent 55%)`,
        }} />
        <div style={{ position: "relative", padding: "26px 24px 22px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <span aria-hidden style={{
              display: "inline-block", width: 8, height: 8, borderRadius: "50%",
              background: PALETTE.amber, boxShadow: `0 0 0 4px rgba(196,130,58,0.18)`,
            }} />
            <span style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: PALETTE.amber, fontWeight: 700, fontFamily: FONT_MONO }}>
              live
            </span>
          </div>

          <DialogTitle asChild>
            <h2 style={{
              fontFamily: FONT_DISPLAY, fontStyle: "italic",
              fontSize: 26, color: PALETTE.ink, lineHeight: 1.2,
              margin: 0, fontWeight: 400,
            }}>
              Talk to us now.
            </h2>
          </DialogTitle>
          <DialogDescription asChild>
            <p style={{ fontSize: 14, color: PALETTE.ink2, marginTop: 8, marginBottom: 18, fontFamily: FONT_BODY }}>
              Drop your number — we'll call you in under a minute.
            </p>
          </DialogDescription>

          {status === "success" ? (
            <div
              role="status"
              style={{
                background: "rgba(196,130,58,0.08)",
                border: `1px solid rgba(196,130,58,0.25)`,
                borderRadius: 14,
                padding: "18px 16px",
                fontFamily: FONT_BODY,
              }}
            >
              <div style={{
                fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                color: PALETTE.amberInk, fontWeight: 700, fontFamily: FONT_MONO, marginBottom: 6,
              }}>
                You're in the queue
              </div>
              <p style={{ margin: 0, color: PALETTE.ink, fontSize: 15, lineHeight: 1.45 }}>
                Expect a call from a local number in under a minute. If you miss
                it, we'll text.
              </p>
              <button
                type="button"
                onClick={() => handleOpenChange(false)}
                style={{
                  marginTop: 14,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  padding: "10px 18px", borderRadius: 999,
                  background: PALETTE.ink, color: "#fff", border: 0,
                  fontFamily: "inherit", fontSize: 13, fontWeight: 600,
                  cursor: "pointer", minHeight: 40,
                }}
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <div style={{ display: "grid", gap: 14 }}>
                <div>
                  <label htmlFor="cmn-fullname" style={labelBase}>Full name</label>
                  <input
                    id="cmn-fullname"
                    ref={firstFieldRef}
                    type="text"
                    required
                    maxLength={80}
                    autoComplete="name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                    placeholder="Alex Rivera"
                    style={{
                      ...fieldBase,
                      border: `1px solid ${showNameErr ? PALETTE.danger : PALETTE.fieldLine}`,
                    }}
                    aria-invalid={showNameErr || undefined}
                  />
                  {showNameErr && (
                    <p style={{ marginTop: 6, marginBottom: 0, fontSize: 12, color: PALETTE.danger, fontFamily: FONT_BODY }}>
                      Please enter your full name.
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="cmn-phone" style={labelBase}>Mobile number (US)</label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "stretch",
                      background: PALETTE.fieldBg,
                      border: `1px solid ${showPhoneErr ? PALETTE.danger : PALETTE.fieldLine}`,
                      borderRadius: 12,
                      overflow: "hidden",
                      minHeight: 48,
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "0 12px",
                        fontFamily: FONT_MONO,
                        fontSize: 14,
                        color: PALETTE.ink2,
                        background: "rgba(0,0,0,0.04)",
                        borderRight: `1px solid ${PALETTE.fieldLine}`,
                        userSelect: "none",
                      }}
                    >
                      🇺🇸 +1
                    </span>
                    <input
                      id="cmn-phone"
                      type="tel"
                      required
                      inputMode="numeric"
                      maxLength={16}
                      autoComplete="tel-national"
                      value={phone}
                      onChange={(e) => setPhone(formatUsPhone(e.target.value))}
                      onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                      placeholder="(555) 123-4567"
                      aria-invalid={showPhoneErr || undefined}
                      style={{
                        flex: 1,
                        padding: "12px 14px",
                        background: "transparent",
                        border: 0,
                        outline: "none",
                        fontSize: 15,
                        fontFamily: FONT_BODY,
                        color: PALETTE.ink,
                        minHeight: 46,
                      }}
                    />
                  </div>
                  {showPhoneErr && (
                    <p style={{ marginTop: 6, marginBottom: 0, fontSize: 12, color: PALETTE.danger, fontFamily: FONT_BODY }}>
                      Enter a valid 10-digit US phone number.
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="cmn-email" style={labelBase}>Email</label>
                  <input
                    id="cmn-email"
                    type="email"
                    required
                    maxLength={255}
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                    placeholder="alex@acme.com"
                    aria-invalid={showEmailErr || undefined}
                    style={{
                      ...fieldBase,
                      border: `1px solid ${showEmailErr ? PALETTE.danger : PALETTE.fieldLine}`,
                    }}
                  />
                  {showEmailErr && (
                    <p style={{ marginTop: 6, marginBottom: 0, fontSize: 12, color: PALETTE.danger, fontFamily: FONT_BODY }}>
                      Enter a valid email address.
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="cmn-business" style={labelBase}>
                    Business name <span style={{ color: PALETTE.muted, textTransform: "none", letterSpacing: 0 }}>(optional)</span>
                  </label>
                  <input
                    id="cmn-business"
                    type="text"
                    maxLength={120}
                    autoComplete="organization"
                    value={business}
                    onChange={(e) => setBusiness(e.target.value)}
                    placeholder="Acme Studio"
                    style={fieldBase}
                  />
                </div>
              </div>

              {status === "error" && errMsg && (
                <p role="alert" style={{
                  marginTop: 12, marginBottom: 0,
                  fontSize: 13, color: PALETTE.danger, fontFamily: FONT_BODY,
                }}>
                  {errMsg}
                </p>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                style={{
                  marginTop: 18,
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                  width: "100%", padding: "14px 22px",
                  background: canSubmit ? PALETTE.amber : "rgba(196,130,58,0.45)",
                  color: "#fff", border: 0, borderRadius: 999,
                  fontFamily: "inherit", fontSize: 14, fontWeight: 600, letterSpacing: "0.01em",
                  cursor: canSubmit ? "pointer" : "not-allowed",
                  minHeight: 48,
                  boxShadow: canSubmit ? "0 12px 28px -14px rgba(196,130,58,0.6)" : "none",
                  transition: "background 180ms ease, box-shadow 180ms ease",
                }}
              >
                {status === "submitting" ? "Connecting…" : "Talk to us now →"}
              </button>

              <p style={{
                marginTop: 12, marginBottom: 0,
                fontSize: 11, lineHeight: 1.5,
                color: PALETTE.muted, fontFamily: FONT_BODY,
              }}>
                By submitting, I agree HireEva by ElectricSheep may contact me
                at the phone number provided using an AI voice call, and that
                the call may be recorded.
              </p>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CallMeNowModal;